import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { nombreDe, useCambiosPersona, type Persona } from './datos';

// Cambiar el código de empleado (#58). Ventana aparte y no un campo más de la
// edición, como la API: el código es el usuario para entrar al sistema, y
// cambiarlo renombra su usuario de Keycloak por la cola (#173). Si Keycloak no
// responde, el cambio se guarda igual y el usuario se renombra cuando vuelva.
//
// Mezclado con «Guardar cambios» del formulario, un código tomado dejaría la
// ficha guardada a medias: el resto sí, el código no.

interface Props {
  persona: Pick<Persona, 'id' | 'firstName' | 'lastName' | 'employeeCode'> | null;
  onCerrar: () => void;
}

export function CambiarCodigoPersona({ persona, onCerrar }: Props) {
  // Montarla al abrir reinicia el campo cada vez.
  return persona ? <Contenido persona={persona} onCerrar={onCerrar} /> : null;
}

function Contenido({ persona, onCerrar }: { persona: NonNullable<Props['persona']>; onCerrar: () => void }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosPersona();

  const form = useFormulario<{ employeeCode: string }>({
    inicial: { employeeCode: persona.employeeCode },
    guardar: (v) => cambios.cambiarCodigo(String(persona.id), v.employeeCode),
    camposPorCodigo: { 'staff.codigo_tomado': 'employeeCode' },
  });

  async function cambiar() {
    // El mismo código no es un cambio: no se molesta a la API ni a Keycloak.
    if (form.valores.employeeCode.trim() === persona.employeeCode) {
      onCerrar();
      return;
    }
    if (!(await form.enviar())) return;
    avisar({
      titulo: t('admin.personal.aviso.codigo', {
        nombre: nombreDe(persona),
        codigo: form.valores.employeeCode.trim(),
      }),
    });
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.personal.codigo.titulo')}
      info={t('admin.personal.codigo.info')}
      descripcion={`${nombreDe(persona)} · ${t('admin.personal.codigo.actual', { codigo: persona.employeeCode })}`}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void cambiar()}>
            {t('admin.personal.codigo.boton')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="employeeCode"
        etiqueta={t('admin.personal.codigo.nuevo')}
        obligatorio
        ancho="completo"
        autoComplete="off"
      />
    </Ventana>
  );
}
